import './App.css';
import * as d3 from "d3";
import { useRef, useEffect } from 'react';
import { useState } from 'react';

// Import the functions you need from the firebase SDKs
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { collection, getDocs, getDoc, addDoc, doc } from "firebase/firestore";
import { getFooterTemplate, getHeaderTemplate } from './utils/pdfTemplates';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyB9P2UONlzELvK7sGPT13L1zMKSq0CRnr0",
  authDomain: "hcp-app-b47d2.firebaseapp.com",
  projectId: "hcp-app-b47d2",
  storageBucket: "hcp-app-b47d2.firebasestorage.app",
  messagingSenderId: "821811288838",
  appId: "1:821811288838:web:4928ce9afe804f6cb61c87",
  measurementId: "G-W87WPH6CTF"
};

const FIRESTORE_COLLECTION_PDF_SEED = 'pdfs_data'

// Initialize Firebase
const app = initializeApp(firebaseConfig);
//const analytics = getAnalytics(app);
const db = getFirestore(app);

// Get programs data
let querySnapshot = await getDocs(collection(db, "hpc_data"));
const docs = []
querySnapshot.forEach((doc) => {
  const doc_data = doc.data()
  doc_data.id = doc.id
  docs.push(doc_data)
});

// Get fields data
const docRef = doc(db, "hpc_fields", "columns")
querySnapshot = await getDoc(docRef);
const fields = querySnapshot.data()
const card_fields = fields['Card Columns']
const tooltip_fields = fields['Tooltip Columns']

const data = { hierarchyData: docs }

// Layout constants
const LAYOUT_CONFIG = {
  // Breakpoints for switching layouts
  BREAKPOINT_SCREEN_MD: 768,
  BREAKPOINT_SCREEN_XS: 320,
  
  // Node rectangle configuration
  rect: {
    xOffset: -1.5,    // rect x position relative to dy
    desktop: {
      width: 3,         // rect width relative to dy
      textX: 0,         // text x position (centered)
    },
    mobile: {
      width: 2.8,       // slightly wider on mobile
      textX: -0.1,      // slight adjustment to center text
    }
  },
  
  // Link styling configuration
  links: {
    desktop: {
      strokeWidth: 8,
      arrowWidth: 2.5
    },
    mobile: {
      strokeWidth: 4,
      arrowWidth: 2.5
    }
  }
}

const color = d3.scaleSequential([0, 1], d3.interpolate('#14967f','#04435c'))

// horizontal bezier curve generator
const linkGen = d3.linkHorizontal().x(d => d.y).y(d => d.x)

// Split text into two lines
const split_text = (text) => {
  if (!text) return [text]
  let words = text.split(' ')
  if (words.length === 1) return [text]
  let half = Math.ceil(words.length / 2)
  return [words.slice(0, half).join(' '), words.slice(half).join(' ')]
}

const set_fontsize = (text, dy, isMobile) => {
  if (!text) return null

  const padding = 5
  const labelAvailableWidth = dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) - padding
  
  // Calculate space needed for the text when wrapped
  const lines = split_text(text)
  const maxLineLength = Math.max(...lines.map(line => line.length))
  const numLines = lines.length
  
  const baseSize = 2.4
  const charWidth = 11
  
  // Calculate space efficiency - how much of available width we're using
  const spaceNeeded = maxLineLength * charWidth
  const spaceEfficiency = spaceNeeded / labelAvailableWidth
  
  // Scale based on both space efficiency and dy
  const dyScale = Math.min(1, dy / 35)  // Scale down when dy is small
  const widthScale = spaceEfficiency > 0.8 ? 
    labelAvailableWidth / (spaceNeeded * 1.2) : 
    labelAvailableWidth / spaceNeeded
  
  // Simple line scaling that considers actual vertical space
  const lineScale = numLines > 1 ? Math.pow(0.9, numLines - 1) : 1
  
  const reductionFactor = Math.min(widthScale * lineScale * dyScale, 1)
  
  // Only reduce from base size if needed
  if (reductionFactor >= 1) {
    return baseSize
  }

  return (baseSize * reductionFactor)
}

  /**
   * Normalize font sizes for tree nodes based on the font size of their 'Opportunity' property.
   * @param {object} hierarchyData - Data used to generate the tree layout.
   * @param {number} dy - Vertical spacing between each node.
   * @param {boolean} isMobile - Whether or not the tree is being rendered on a mobile screen.
   * @returns {number} The normalized font size to use for tree nodes (em units need to be added afterwards).
   */
  const dataNormalizedFontSize = (hierarchyData, dy, isMobile) => {
  let normalizedFontSize = 1.2

  // Collect font sizes, ensuring they are valid numbers
  const nodeFontSizes = hierarchyData
    .map(d => {
      const nodeFontSize = set_fontsize(d['Opportunity'], dy, isMobile);
      return nodeFontSize;
    })
    .filter(fs => !isNaN(fs) && fs > 0);  // Remove invalid values

  // Extract the smallest valid font size
  const minFontSize = nodeFontSizes.length > 0 ? Math.min(...nodeFontSizes) : 1.2;  // Default if no valid sizes

  // Ensure minFontSize doesn't go below a reasonable threshold
  normalizedFontSize = Math.max(minFontSize, 1.1);

  return normalizedFontSize
}

// Create markers for arrowheads
const make_marker = (id, marker_color, isMobile) => {
  const aw = (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)
  d3.select('defs').append('marker')
    .attr('id', id)
    .attr('viewBox', [ 0, -aw/2, aw, aw])
    .attr('refX', 0)
    .attr('refY', 0)
    .attr('markerWidth', aw)
    .attr('markerHeight', aw)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', `M 0 ${aw/2} L ${aw} 0 L 0 ${-aw/2} L ${aw/10} 0 z ` )
    .attr('stroke-width', 0.1)
    .attr('stroke', 'black')
    .attr('fill', marker_color)
}

const createTooltip = () => {
  let Tooltip = d3.select("#svgContainer")
      .append("div")
      .attr("class", "tooltip")
      .attr("id", "tooltip")
      .style("position", "absolute")
      .style("opacity", 0)
      .style("pointer-events", "none")
  return Tooltip
}

const tooltipHtml = (item) => {
  //console.log(item)
  let html_string = `
    <div class="card mb-1">
    <h5 class="card-header">${item.Opportunity}</h5>
    <div class="card-body">
    `
  let link = tooltip_fields.includes('Link') ? item.Link : null
  tooltip_fields.forEach(field => {
    if (!item[field]) return
    if (field === 'Link') return   
    html_string += `<p class='mb-1'><strong>${field}:</strong> ${item[field]}</p>`
  })
  if (link && link !== '') {
    html_string += `<hr class="hr" /><a href="${link}" target="_blank" class="btn learnMoreButton">Learn More</a>`
  }
  html_string += `</div></div>` 
  return html_string
}

// Show tooltip
const showTooltip = (event, d) => {
  let Tooltip = d3.select("#tooltip")
  if (Tooltip.empty()) Tooltip = createTooltip()
  
  Tooltip
    .html(tooltipHtml(d.data))
    .classed("visible", true)
    .style("opacity", 1)
    .style("visibility", "visible")
  
  positionTooltip(event, d)
}

// Position tooltip
const positionTooltip = (event, d) => {
  const Tooltip = d3.select("#tooltip")
  if (!Tooltip.node()) return
  
  const isMobile = window.innerWidth < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  
  // Get dimensions
  const tooltipWidth = Tooltip.node().getBoundingClientRect().width
  const tooltipHeight = Tooltip.node().getBoundingClientRect().height
  
  // Calculate position relative to the viewport
  const rect = event.currentTarget.getBoundingClientRect()
  const left = rect.left + window.scrollX
  const top = rect.top + window.scrollY
  
  // Calculate maximum allowed width for tooltip
  const maxWidth = Math.max(left - 20, isMobile ? window.innerWidth * 0.8 : window.innerWidth * 0.5)
  
  // Set tooltip width if needed
  if (tooltipWidth > maxWidth) {
    Tooltip.style("width", maxWidth + "px")
  }
  
  // Calculate vertical position ensuring tooltip stays in viewport
  let tooltipTop = top - tooltipHeight/2
  const minTop = window.scrollY + 10 // Keep 10px from top of viewport
  const maxTop = window.scrollY + window.innerHeight - tooltipHeight - 10 // Keep 10px from bottom
  tooltipTop = Math.max(minTop, Math.min(tooltipTop, maxTop))
  
  // Position tooltip
  Tooltip
    .style("left", (left - tooltipWidth) + "px")
    .style("top", tooltipTop + "px")
}

const hideTooltip = () => {
  const Tooltip = d3.select("#tooltip")
  if (!Tooltip.empty()) {
    Tooltip
      .classed("visible", false)
      .style("opacity", 0)
      .style("visibility", "hidden")
  }
}

// Position nodes
const reposition_nodes = (d, source, width, height, dx, dy) => {
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const xFactor = 1.5
  const yFactor = isMobile ? 1.5 : 3

  let xstart = xFactor*dy
  let ystart = width - yFactor*dy

  if (d.data['Opportunity Type'] === 'Next Degree') {
    d.y = source ? yFactor*dy : width/2
    d.x = xstart
    if (d.id !== 'High School') {
      let depth = d.depth
      d.y = d.parent.y
      d.x = d.parent.x + 2*dy
    }
  } else if (d.data['Opportunity Type'] === 'Educational Opportunity') {
    let childIndex = d.parent.children.filter(c => c.data['Opportunity Type'] === 'Educational Opportunity').findIndex(c =>
      c.id === d.id)
    d.y = ystart
    //d.x0 = (childIndex)*100
    d.x = xstart + childIndex*1.1*dy
    //d.x = d.parent.x + childIndex*0.8*dy
    //d.y0 = d.parent.y0
  } else if (d.data['Opportunity Type'] === 'Job Opportunity') {
    let childIndex = d.parent.children.filter(c => c.data['Opportunity Type'] === 'Job Opportunity').findIndex(c => c.id === d.id)
    //let xstart = -d.parent.children.filter(c => c.data['Opportunity Type']==='Job Opportunity').length*0.8*dy - 0.8*dy
    //ystart += ystart/3 + d.parent.children.filter(c => c.data['Opportunity Type'] === 'Educational Opportunity').length*0.8*dy //+ 0.8*dy
    d.y =  2*ystart
    d.x = xstart + childIndex*0.8*dy
    //d.x = d.parent.x + childIndex*0.8*dy // + 1.5*dy + arrowWidth*strokeWidth + 1
  }
  return d
}

const update = (source, rootNode, tree, width, height, dx, dy, normalizedFontSize) => {
  console.log('Updating nodes')
  console.log('Source Node:', source)
  console.log('Root Node:', rootNode)
  
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const svg = d3.select("svg")

  // If the source node has the children hidden, show them
  if (source && !source.children) source.children = source._children

  // get direct descendants (not all descendants)
  let nodes = rootNode.descendants().reverse();
  let links = rootNode.links()
  //console.log('Nodes:', nodes)

  // Filter node and links to only include those that are children of source or Degree nodes
  if (source) {
    nodes = nodes.filter(n => 
      n.data['Opportunity Type'] !== 'Job Opportunity' && 
      ( (n.parent && n.parent.id === source.id) || (n.data['Opportunity Type'] === 'Next Degree') )
    )
    links = links.filter(l => l.source.id === source.id )
  } else {
    nodes = nodes.filter(n => n.data['Opportunity Type'] === 'Next Degree')
    links = []
  }
  
  tree(rootNode);
  rootNode.descendants().forEach((d, i) => {
    d = reposition_nodes(d, source, width, height, dx, dy)
  });
  //console.log('Root Node:', rootNode)

  // find top most and bottom most leaf positions
  let left = rootNode;
  let right = rootNode;
  rootNode.eachBefore(node => {
    if ( !nodes.map(n => n.id).includes(node.id) ) return
    if (node.x < left.x)  left = node ;
    if (node.x > right.x) right = node;
  });
  //console.log('Root Node:', rootNode)
  // recompute svg height
  height = right.x - left.x + 2.5 * dy;

  // transition svg size and viewbox
  const transition = svg.transition()
  .duration(1000)
  .attr("viewBox", [0, 0, width, height])
  .tween("resize", window.ResizeObserver ? null : () => () => svg.dispatch("toggle"));  

  const gradientEnd = width - 3*dy - dx    

  links = links.filter(l => {
    if (l.target.data['Opportunity Type']==='Next Degree') return false
    if (l.target.data['Opportunity Type']==='Job Opportunity') return false
    // if (l.target.data['Opportunity Type'] === 'Job Opportunity') {
    //   if (l.target.id === l.target.parent.children.filter(c => c.data['Opportunity Type'] === 'Job Opportunity')[0].id) return true
    // }
    if (l.target.data['Opportunity Type'] === 'Educational Opportunity') {
      return true
      //if (l.target.id === l.target.parent.children.filter(c => c.data['Opportunity Type'] === 'Educational Opportunity')[0].id) return true
    }
    return false
   })
  //console.log('Links:', links)


  // Assign link target ids to links as data. Update with transitions on enter and exit
  const gLink = svg.select(".links")
  const gLinks = gLink.selectAll("path")
    .data(links, d => d.target.id)
    .join(
      enter => enter.append("path")
        .attr("fill", "none")
        .attr("stroke", 'url(#color-gradient)')
        .attr("stroke-opacity", 1)
        .each(d => make_marker(d.target.id, color(d.target.y/gradientEnd), isMobile))
        .attr("marker-end", d => `url("#${d.target.id}")`)
        //.attr("display", "none") // Getting rid of arrow/links for now
        .attr("stroke-width", d => {
          const sw = isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth
          return d.source.data['Opportunity Type']==='Next Degree' ? sw : sw / 2
        })
        .attr("d", d => {
          if (source) return linkGen({ source: { x: source.x0, y: source.y0 }, target: { x: source.x0, y: source.y0 } }) 
          return linkGen({ source: { x: d.source.x, y: d.source.y }, target: { x: d.target.x, y: d.target.y } })
         })
        .call(enter => enter.transition(transition)
          .attr("d", d => {
            const origin = { x: d.source.x, y: d.source.y }
            //const origin = { x: d.source.x, y: d.source.y }
            //const target = { x: d.target.x, y: d.target.y }
            const target = {
              x: d.target.data['Opportunity Type']==='Next Degree' ? d.target.x - dy/2 - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) : d.target.x  , 
              y: d.target.data['Opportunity Type']==='Next Degree' ? d.target.y : d.target.y + dy * (isMobile ? LAYOUT_CONFIG.rect.xOffset : LAYOUT_CONFIG.rect.xOffset) - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) 
            }
            return linkGen({ source: origin, target: target })
          })
        ),
      update => update
        .call(update => update.transition(transition)
          //.attr("d", linkGen)
          //.attr("marker-end", "url(#arrow)")
          .attr("d", d => {
            const origin = { x: d.source.x, y: d.source.y }
            //const target = { x: d.target.x, y: d.target.y }          
            const target = {
               x: d.target.data['Opportunity Type']==='Next Degree' ? d.target.x - dy/2 - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) : d.target.x ,//+ 1.5*dy + arrowWidth*strokeWidth + 1
               y: d.target.data['Opportunity Type']==='Next Degree' ? d.target.y : d.target.y + dy * (isMobile ? LAYOUT_CONFIG.rect.xOffset : LAYOUT_CONFIG.rect.xOffset) - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) 
            }
            return linkGen({ source: origin, target: target });
          })
        ),
      exit => exit
        .call(exit => exit.transition(transition) // Transition exiting nodes to the parent's new position.  
          .remove()
          .attr("d", d => {
            const o = { x: d.source.x, y: d.source.y }
            //const o = source ? { x: source.x, y: source.y } : { x: rootNode.x, y: rootNode.y }
            return linkGen({ source: o, target: o });
          })
        )
    )

    ///Add text to links
    const textedLinks = [ rootNode.links().filter(l => l.target.data['Opportunity Type'] === 'Next Degree')[0] ]
    if (links.length) textedLinks.push( links.filter(l => l.target.data['Opportunity Type'] === 'Educational Opportunity')[0] )
    gLink.selectAll('text')
      .data(textedLinks, d => d.target.id)
      .join(
        enter => enter.insert('text', 'path')
          .classed('link-text', true)   
          .text(d => d.target.data['Opportunity Type']==='Next Degree' ? 'Select degree level:': d.target.data['Opportunity Type']==='Job Opportunity' ? 'Select a Jobs' : 'Select any opportunities:')  
          .attr("x", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.y : d.source.y0 )
          .attr("y", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.x - dy : d.source.x0 )
          .attr("text-anchor", "middle")//d._children ? "end" : "start")
          //.attr("transform", d => d.target.data['Opportunity Type']==='Next Degree' ? '' : 'rotate(-90)') 
          .attr("font-family", "Sofia Pro Regular")
          .attr("fill", d => source ? color(d.target.y/gradientEnd) : color(0) )
          .attr("stroke-width", 0.1)
          .attr("opacity", 1)
          .call(enter => enter.transition(transition)
            //.attr("opacity", 1)
            .attr("x", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.y : d.target.y ) //d._children ? -6 : 6)
            .attr("y", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.x - dy : rootNode.x - dy)// d.target.x - dy/2 )
          ),
        update => update
          .call(update => update.transition(transition)
          //.attr("opacity", 1)
            .attr("x", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.y : d.target.y ) //d._children ? -6 : 6)
            .attr("y", d => d.target.data['Opportunity Type']==='Next Degree' ? rootNode.x - dy : rootNode.x - dy) //d.target.x - dy/2)
          ),
          exit => exit
            .call(exit => exit.transition(transition)
              .remove()   // Transition exiting nodes to the parent's new position.
              .attr("transform", d => `translate(${-d.source.y},${d.source.x})` )
              .attr("opacity", 0)
          )
      )
    

    // Assign node ids as data. Update with transitions on enter and exit 
    const gNode = svg.select(".nodes")
    const gNodes = gNode.selectAll('g')
      .data(nodes, d => d.id)
    gNodes.join(
      enter => enter.append('g')
        .attr("cursor", "pointer") //d => d.data['Opportunity Type']==='Next Degree' ? "pointer" : "default")
        .attr("pointer-events", "all") //d => d.data['Opportunity Type']==='Next Degree' ? "all" : "none")
        .attr("transform", d => source ? `translate(${source.y0},${source.x0})` : `translate(${d.y},${d.x})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .classed('degree', d => d.data['Opportunity Type']==='Next Degree')
        .classed('job', d => d.data['Opportunity Type']==='Job Opportunity')
        .classed('edop', d => d.data['Opportunity Type']==='Educational Opportunity')
        .call(enter => enter.append("rect")
          .attr('x', d => dy * LAYOUT_CONFIG.rect.xOffset)
          .attr('y', d => d.data['Opportunity Type']==='Next Degree' ? -dy/2 : -dy/2)
          .attr('width', d => dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width))
          .attr('height', d => d.data['Opportunity Type']==='Next Degree' ? dy : dy)
          .attr("rx", d => d.data['Opportunity Type']==='Next Degree' ? 0 : dy/3) //d.data['Opportunity Type']==='Educational Opportunity' ? dy/5 : dy/3)
          .attr("stroke-width", d => d.data['Opportunity Type']==='Next Degree' ? 2 : 1)
          .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0))
          .attr('fill', '#f7f7f9')
          //.attr("fill", d => d.id === source.id ? color(d.y/gradientEnd) : '#f7f7f9')
          //.classed('selected', d => d.id === source.id)
          //.attr('fill', '#f7f7f9')
        )
        .call(enter => enter.append("text")
          .attr("x", d => dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.textX : LAYOUT_CONFIG.rect.desktop.textX))
          .attr("y", d => d.id.includes('>') ? d.id.split('>')[1].split(' ').length > 1 ? "-0.2em" : "0.3em" : d.id.split(' ').length > 1 ?  "-0.2em" : "0.3em")
          .attr("text-anchor", "middle")//d._children ? "end" : "start")
          .attr("font-family", "Sofia Pro Regular")
          .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke-width', 0.25)
          .style("font-size", `${normalizedFontSize}em`)
          .text(d => {
            let text = d.id.includes('>') ? d.id.split('>')[1] : d.id
            let lines = split_text(text)
            return lines[0]
          })
          
        )
        .call(enter => enter.append("text")
          .attr("x", d => dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.textX : LAYOUT_CONFIG.rect.desktop.textX))
          .attr("y", "0.9em")
          .attr("text-anchor", "middle")//d._children ? "end" : "start")
          .attr("font-family", "Sofia Pro Regular")
          .attr('fill',  d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke-width', 0.25)
          .style("font-size", `${normalizedFontSize}em`)
          .text(d => {
            let text = d.id.includes('>') ? d.id.split('>')[1] : d.id
            let lines = split_text(text)
            return lines.length > 1 ? lines[1] : null
           })
        )
        .call(enter => enter.transition(transition)
          .attr("transform", d => `translate(${d.y},${d.x})`)
          .attr("fill-opacity", 1)
          .attr("stroke-opacity", 1)
        )
        .on("click", (event, d) => {         // recursively update on click event,  toggle switch for turning children on and off
            //console.log('Selected Node:', d3.select('.selected').data()[0] && d3.select('.selected').data()[0].id === d.id)
            if(d.data['Opportunity Type'] !== 'Next Degree') {
              //addToPla(d.data)
              return
            }
            d.children = d.children > 1 ? d._children.filter(c => c.data['Opportunity Type']==='Next Degree' ) : d._children 
            if (d.depth === 0) { 
              rootNode.children = d.children
            } else {
              rootNode.find(node => node.id === d.id).children = d.children 
            }
            // Stash the old positions for transition.
            rootNode.eachBefore(d => {
              // position before next transition equal to current position
              d.x0 = d.x;
              d.y0 = d.y;
            });
            d3.selectAll('rect')
              .attr('stroke-width', 2)
              .attr('fill', '#f7f7f9' )
              .attr("filter", 'none')
            d3.selectAll('text').transition()
              .duration(100)
              .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
              .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0) )
            if (d3.select('.selected').data()[0] && d3.select('.selected').data()[0].id === d.id) {
              d3.selectAll('rect').classed('selected', false)
              update(null, rootNode, tree, width, height, dx, dy, normalizedFontSize)
            } else {
              d3.selectAll('rect').classed('selected', false)
              d3.select(event.target.parentNode).select('rect').classed('selected', true)
                .transition()
                .duration(100)
                .attr('stroke-width', 3)
                .attr('fill', d => source ? color(d.y/gradientEnd) : color(0))
              d3.select(event.target.parentNode).selectAll('text').transition()
                .duration(100)
                .attr('fill', 'white' )
                .attr('stroke', 'white' )
              update(d, rootNode, tree, width, height, dx, dy, normalizedFontSize);
            } 
        })
        .on("mouseover", (event, d) => {
          d3.select(event.target.parentNode).select('rect').transition()
            .duration(100)
            .attr('stroke-width', 3)
            .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
            .attr("filter", "url(#dropshadow)")
          d3.select(event.target.parentNode).selectAll('text').transition()
            .duration(100)
            .attr('fill', 'white' )
            .attr('stroke', 'white' )
          d.data['Opportunity Type'] !== 'Next Degree' && showTooltip(event, d)
        })
        .on("mouseout", (event, d) => {
          d3.select(event.target.parentNode).select('rect').transition()
            .duration(100)
            .attr("filter", "none")
          d.data['Opportunity Type'] !== 'Next Degree' && hideTooltip()
          if (d3.select(event.target.parentNode).select('rect').classed('selected')) return
          d3.select(event.target.parentNode).select('rect').transition()
            .duration(100)
            .attr('stroke-width', d => d.data['Opportunity Type']==='Next Degree' ? 2 : 1)
            .attr("fill", '#f7f7f9')
          d3.select(event.target.parentNode).selectAll('text').transition()
            .duration(100)
            .attr('fill', d => source ? color(d.y/gradientEnd) : color(0))
            .attr('stroke',d => source ? color(d.y/gradientEnd) : color(0))
        }),
      update => update
        .call(update => update.transition(transition)
          .attr("transform", d => `translate(${d.y},${d.x})`)
          .attr("fill-opacity", 1)
          .attr("stroke-opacity", 1)
        ),
      exit => exit
        .call(exit => exit.transition(transition)
          .remove()   // Transition exiting nodes to the parent's new position.
          .attr("transform", d => `translate(${d.parent.y},${d.parent.x})` )
          .attr("fill-opacity", 0)
          .attr("stroke-opacity", 0)
        )
    )
  // Stash the old positions for transition.
  rootNode.eachBefore(d => {
    // position before next transition equal to current position
    d.x0 = d.x;
    d.y0 = d.y;
  });
}

const drawChart = (data, svgContainerRef, viewSize) => {
  console.log('Drawing chart')

  const width = viewSize.width - 12  // 12 is the x-padding
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const isExtraSmall = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_XS
  const nextDegreeData = data.hierarchyData.filter(item => item['Opportunity Type'] === 'Next Degree');
  const height = isExtraSmall ? nextDegreeData.length * 168 :
               isMobile ? nextDegreeData.length * 192 : 
               nextDegreeData.length * 320

  const dx = width/3
  const dy = height/(2*8)
  const gradientEnd = width - 3*dy - dx

  console.log('Width:', width, 'Height:', height, 'dx:', dx, 'dy:', dy)

  const tree = d3.tree().nodeSize([dx, dy])

  let svg = d3.select("svg")

  if (svg.empty()) {
    console.log('No svg element found creating...')
    svg = d3.select(svgContainerRef.current).append("svg")
      .attr("viewBox", [0, 0, width, height/2])
      .style("font", "10px sans-serif")
      .style("user-select", "none")
  } else {
    svg.selectAll('*').remove()
  }

  // Add color gradient
  svg.append("linearGradient")
    .attr("id", "color-gradient")
    .attr("gradientUnits", "userSpaceOnUse")
     .attr("x1", width/2.5)
     .attr("y1", 0)
     .attr("x2", gradientEnd)
     .attr("y2", 0)
  .selectAll("stop")
    .data(d3.ticks(0, 1, 10))
  .join("stop")
    .attr("offset", d => d)
    .attr("stop-color", color.interpolator());

  const filter = svg.append("filter")
      .attr("id", "dropshadow")
  filter.append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 4)
      .attr("result", "blur")
  filter.append("feOffset")
      .attr("in", "blur")
      .attr("dx", 2)
      .attr("dy", 2)
      .attr("result", "offsetBlur");
  const feMerge = filter.append("feMerge")
  feMerge.append("feMergeNode")
      .attr("in", "offsetBlur")
  feMerge.append("feMergeNode")
      .attr("in", "SourceGraphic")

  // Add defs for arrowhead markers
  svg.append('defs')
  
  // group links
  svg.append("g")
    .classed('links', true)
    //.attr("cursor", "pointer")
    //.attr("pointer-events", "all")

  // group nodes
  svg.append('g')
    .classed('nodes', true)
    //.attr("cursor", "pointer")
    //.attr("pointer-events", "all")

  let selectedCareer = 'Health'
  //let selectedCareer = 'Education'
  let hierarchyData = data.hierarchyData.filter(r => r.Career === null || r.Career === selectedCareer)
  hierarchyData = hierarchyData.filter(r => r['Opportunity Type'] !== 'Ignore')
  hierarchyData.push({ Career: selectedCareer, Degree: '', 'Opportunity Type': 'Next Degree', Opportunity: 'High School' })
  let degrees = [... new Set(hierarchyData.map(d => d.Degree))]
  hierarchyData = hierarchyData.filter(d => d['Opportunity Type'] !== 'Next Degree' || degrees.includes(d['Opportunity']))
  console.log('Hierarchy Data:', hierarchyData)
  const normalizedFontSize = dataNormalizedFontSize(hierarchyData, dy, isMobile)

  let rootData = d3.stratify()
    .id(d => {
      let id = undefined
      if (d['Opportunity Type']==='Next Degree') id = d['Opportunity'] 
      else if ( d['Opportunity Type']==='Job Opportunity') id = d.Degree + '>' + d['Opportunity']
      else if ( d['Opportunity Type']==='Educational Opportunity') id = d.Degree + '>' + d['Opportunity']
      return id
     })
    .parentId(d => d.Degree )
    (hierarchyData)
    
  // Wrap data in tree attributes and methods
  const rootNode = tree(rootData)
  console.log('Root Node:', rootNode)

  // Reposition nodes, this has to be done for all 
  // nodes before hiding their children
  rootNode.descendants().forEach(d => {
    d = reposition_nodes(d, null, width, height, dx, dy)
  });
  // add id and child copy for each node
  
  // stash children for transitions
  rootNode.descendants().forEach(n => {
    n._children = n.children 
  })

  // assign rootNode node position
  rootNode.x0 = 0;
  rootNode.y0 = 0;

  // update chart
  //let selectedDegree = 'High School' 
  //let selectedNode = rootNode.find(node => node.id === selectedDegree)
  update(null, rootNode, tree, width, height, dx, dy, normalizedFontSize)
}


const Viz = ({ addToPlan, data}) => {
  const svgContainerRef = useRef(null);
  const [preventRedraw, setPreventRedraw] = useState(false)
  const [viewSize, setViewSize] = useState({ width: window.innerWidth , height: window.innerHeight })
  
  const handleResize = () => {
    setViewSize({ width: window.innerWidth, height: window.innerHeight })
  }

  useEffect(() => {
    if (svgContainerRef.current && !preventRedraw) {
      drawChart(data, svgContainerRef, viewSize)
    }
  }, [viewSize.width]);

  useEffect(() => {
    window.addEventListener('resize', handleResize)
      
    // cleanup this component
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleClick = (event) => {
    let node = d3.select(event.target.parentNode)
    if (node.classed('edop')) {
      setPreventRedraw(true)
      addToPlan(node.data()[0].data)
    }
  }

  return (
    <div className="container-fluid py-2" id="svgContainer" onClick={handleClick} ref={svgContainerRef}></div>    
  )
}

function App() {
  const planRef = useRef(null);
  const [currentPlan, setCurrentPlan] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('');

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailClick = () => {
    setEmailInput('');
    setEmailError('');
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail(emailInput)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setShowEmailModal(false);
    await handleEmail(emailInput);
  };

  const showToastMessage = (text) => {
    setToastText(text)
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }

  const handleEmail = async (email) => {
    console.log('Handling email')
    try {
      // Get timestamp in milliseconds
      const timestamp = Date.now();
      
      // Create filename
      const outputFileName = `HealthCareerPath-${email}.pdf`;

      let degrees = [...new Set(currentPlan.map(i => i.Degree))]

      // Add timestamp to the data
      const dataToStore = {
        currentPlan: currentPlan,
        degrees: degrees,
        created: new Date().toISOString(),
        destinationEmail: email,
        _pdfplum_config: {
          outputFileName: outputFileName,
          adjustHeightToFit: false,
          chromiumPdfOptions: {
            format: "Letter",
            printBackground: true,
            displayHeaderFooter: true,
            headerTemplate: getHeaderTemplate(),
            footerTemplate: getFooterTemplate(),          
            margin: {top: "0in", right: "1in", bottom: "0.5in", left: "1in"}
          }
        }
      };
      // Add document to pdfs_data collection
      const docRef = await addDoc(collection(db, FIRESTORE_COLLECTION_PDF_SEED), dataToStore);
      console.log("Document written with ID: ", docRef.id);
      showToastMessage("Email sent")
    } catch (error) {
      console.error("Error saving plan: ", error);
    }
  }

  const addToPlan = (item) => {
    console.log('Adding to plan:', item)
    showToastMessage("Opportunity added")
    setCurrentPlan([...currentPlan, item])
  } 

  const handleClear = () => {
    showToastMessage("Plan cleared")
    setCurrentPlan([])
  }

  //Remove single planItem from plan
  const removeFromPlan = (itemToRemove) => {
    showToastMessage("Opportunity removed")
    setCurrentPlan(currentPlan.filter(item => item !== itemToRemove));
  }

  const planItemCard = (item, index) => {
    return (
      <div className="card mb-1" key={index}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">{item.Opportunity}</h5>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Remove from plan"
            title='Remove from plan'
            onClick={(e) => {
              removeFromPlan(item);
              e.target.blur();
            }}
          ></button>
        </div>
        <div className="card-body">
          {card_fields.map((field, index) => {
            if (item[field] && field !== 'Link'){
              return <p className='mb-1' key={index}><strong>{field}:</strong> {item[field]}</p>
            }
            return null
          })}
          {card_fields.includes('Link') &&
           <div>
            <hr className="hr" />
            <a href={item['Link']} target="_blank" rel='noreferrer' className="btn learnMoreButton">Learn More</a>
           </div>
           //<p className='mb-1'><a href={item['Link']} className="card-link" target='_blank' rel="noreferrer"><strong>{'Link'}:</strong> {item['Link']}</a></p>
          }    
        </div>
      </div>
    )
  }


  const renderPlanItems = (currentPlan, planRef) => {
    let degrees = [...new Set(currentPlan.map(i => i.Degree))]
    if (currentPlan.length) return(
      <div id="plan" className='container-fluid mb-5' ref={planRef} >
        <div className="container plan-header text-center">
          <h1 className='plan-title'>Your Pathway</h1>
        </div>
        <div className="container plan-footer d-flex justify-content-center my-3">
          <button type="button" className="btn emailButton mx-2" onClick={handleEmailClick}>Email it</button>
          <button type="button" className="btn clearButton mx-2" onClick={handleClear}>Clear</button>
        </div>
        <div className="plan-body row">
          {
            degrees.map((degree, index) => (
              <div className="col-md-3" key={index}>
                <h3 className='text-center'>{degree}</h3>
                {currentPlan.filter(item => item.Degree === degree).map((item, index) => (
                  planItemCard(item, index)  
                ))}
              </div>
            ))
          }
        </div>
      </div>
    ) 
  }

  const renderEmailModal = () => (
    <>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          cursor: 'pointer',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw'
        }}
      ></div>
      <div 
        className="modal show" 
        tabIndex="-1" 
        role="dialog" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          width: '100vw'
        }}
        onClick={() => setShowEmailModal(false)}
      >
        <div 
          className="modal-dialog" 
          onClick={e => e.stopPropagation()}
          style={{
            margin: '2rem auto',
            maxWidth: '32rem',
            width: '90%',
            transform: 'translate(0, 0)'
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title card-header justify-content-center">Email Your Pathway</h5>
              <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="email"
                  className={`form-control ${emailError ? 'is-invalid' : ''}`}
                  placeholder="Please enter email address"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEmailSubmit();
                    }
                  }}
                />
                {emailError && <div className="invalid-feedback">{emailError}</div>}
              </div>
            </div>
            <div className="modal-modal-footer mb-2">
              <button type="button" className="btn sendButton mx-2" onClick={handleEmailSubmit}>Send</button>
              <button type="button" className="btn cancelButton mx-2" onClick={() => setShowEmailModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  const renderToast = () => (
    <div 
      className="alert alert-success d-flex align-items-center"
      style={{
        position: 'fixed',
        top: '5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        padding: '0.5rem 1rem',
        margin: 0,
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
        animation: 'slideFromTop 3s ease-in-out'
      }}
      role="alert"
    >
      <i className="bi bi-check-circle-fill me-2"></i>
      {toastText}
    </div>
  )

  return (
    <>    
      <Viz data={data} addToPlan={addToPlan} />
      {renderPlanItems(currentPlan, planRef)}
      {showEmailModal && renderEmailModal()}
      {showToast && renderToast()}
    </>
  );
}

export default App;
